import * as React from "react"
import { Link, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import {
  HomeIcon,
  BuildingIcon,
  SettingsIcon,
  UsersIcon,
  CreditCardIcon,
  PanelLeftIcon,
  FolderIcon,
  GlobeIcon,
} from "lucide-react"

import { NavUser } from "@/components/nav-user"
import { OrganizationSwitcher } from "@/components/organization-switcher"
import { Logo } from "@/components/logo"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  useSidebar,
} from "@/components/ui/sidebar"
import { Button } from "@/components/ui/button"
import { isAdmin as checkIsAdmin, isExternal as checkIsExternal } from "@/lib/auth"

interface User {
  id: number
  name: string
  email: string
  admin: boolean
  super_admin?: boolean
  organization_role: string
}

interface OrganizationSidebarProps extends React.ComponentProps<typeof Sidebar> {
  user: User
}

export function OrganizationSidebar({ user, ...props }: OrganizationSidebarProps) {
  const { url } = usePage()
  const { toggleSidebar } = useSidebar()
  const { t } = useTranslation('common')

  const isActive = (path: string, exact = false) => {
    if (exact) return url === path
    return url.startsWith(path)
  }

  const isAdmin = checkIsAdmin(user)
  const isExternal = checkIsExternal(user)

  return (
    <Sidebar collapsible="icon" variant="floating" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem className="relative">
            {/* Expanded state: Logo links home, toggle button on right */}
            <SidebarMenuButton size="lg" asChild className="hover:bg-transparent active:bg-transparent data-[active=true]:bg-transparent group-data-[collapsible=icon]:hidden">
              <Link href="/">
                <div className="flex flex-1 items-center">
                  <Logo className="h-6" />
                </div>
              </Link>
            </SidebarMenuButton>
            {/* Collapsed state: Logo icon, changes to sidebar icon on hover */}
            <SidebarMenuButton
              size="lg"
              className="hover:bg-accent hidden group-data-[collapsible=icon]:flex justify-center group/logo"
              onClick={toggleSidebar}
              tooltip={t('sidebar.expandSidebar')}
            >
              <Logo iconOnly className="h-6 w-6 group-hover/logo:hidden" />
              <PanelLeftIcon className="h-5 w-5 hidden group-hover/logo:block" />
            </SidebarMenuButton>
            {/* Collapse toggle button - only visible when expanded */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-7 w-7 group-data-[collapsible=icon]:hidden"
              onClick={toggleSidebar}
            >
              <PanelLeftIcon className="h-4 w-4" />
              <span className="sr-only">{t('sidebar.toggleSidebar')}</span>
            </Button>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('sidebar.backToProjects')}>
                  <Link href="/projects">
                    <HomeIcon />
                    <span>{t('sidebar.home')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Organization Section */}
        <SidebarGroup>
          <SidebarGroupLabel>{t('sidebar.organization')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {!isExternal && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip={t('sidebar.overview')} isActive={isActive("/organization", true) || isActive("/organization/edit")}>
                    <Link href="/organization">
                      <BuildingIcon />
                      <span>{t('sidebar.overview')}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {isAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip={t('sidebar.projects')} isActive={isActive("/organization/projects")}>
                    <Link href="/organization/projects">
                      <FolderIcon />
                      <span>{t('sidebar.projects')}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {!isExternal && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip={t('sidebar.members')} isActive={isActive("/organization/members")}>
                    <Link href="/organization/members">
                      <UsersIcon />
                      <span>{t('sidebar.members')}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {isAdmin && (
                <>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip={t('sidebar.domains')} isActive={isActive("/organization/domains")}>
                      <Link href="/organization/domains">
                        <GlobeIcon />
                        <span>{t('sidebar.domains')}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip={t('sidebar.settings')} isActive={isActive("/organization/settings")}>
                      <Link href="/organization/settings">
                        <SettingsIcon />
                        <span>{t('sidebar.settings')}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip={t('sidebar.subscription')} isActive={isActive("/organization/subscription")}>
                      <Link href="/organization/subscription">
                        <CreditCardIcon />
                        <span>{t('sidebar.subscription')}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Spacer to push footer to bottom */}
        <div className="flex-1" />

      </SidebarContent>

      <SidebarFooter>
        <OrganizationSwitcher />
        <NavUser user={user} hideUpgradeButton={true} />
      </SidebarFooter>
    </Sidebar>
  )
}
